import { createContext } from 'react';

import { hasArticleShipmentNextTask } from '@/components/ArticlesTableCells/ArticleShipmentNextTaskCell';
import Box from '@/design_system/Box';
import { canReceptionBeVerifiedBy, ShipmentWithRelations } from '@/models/shipment';
import {
  ArticlesCardList,
  ArticlesTable,
} from '@/routes/Shipments/Shipment/components/ArticlesTable/ArticlesTable';
import { useCurrentSession } from '@/services/auth';
import useViewPort from '@/utils/useViewport';

interface ShipmentArticlesContextData {
  showVerificationActions: boolean;
  showVerificationStatus: boolean;
  showPrintArticleSheetActions: boolean;
  showDeleteArticleAction: boolean;
  showNextTaskColumn: boolean;
  showActionsColumn: boolean;
  shipment: ShipmentWithRelations;
}

export const ShipmentArticlesContext = createContext({} as ShipmentArticlesContextData);

export const ShipmentArticles = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const { isMobile } = useViewPort();
  const { currentSession } = useCurrentSession();

  const showVerificationActions =
    shipment.step === 'verification' && canReceptionBeVerifiedBy(shipment, currentSession);
  const showVerificationStatus = showVerificationActions || shipment.step === 'validated';
  const showPrintArticleSheetActions = shipment.step === 'preparation';
  const showDeleteArticleAction = shipment.step === 'preparation';
  const showNextTaskColumn = !!shipment.articles.some((shipmentArticle) =>
    hasArticleShipmentNextTask({ article: shipmentArticle.article })
  );
  const showActionsColumn =
    showVerificationActions ||
    showPrintArticleSheetActions ||
    showDeleteArticleAction ||
    showNextTaskColumn;

  const contextValue = {
    showVerificationActions,
    showVerificationStatus,
    showPrintArticleSheetActions,
    showDeleteArticleAction,
    showNextTaskColumn,
    showActionsColumn,
    shipment,
  } satisfies ShipmentArticlesContextData;

  return (
    <ShipmentArticlesContext value={contextValue}>
      <Box padding="0" removeStyleOnMobile>
        {isMobile ? (
          <ArticlesCardList shipmentArticles={shipment.articles} />
        ) : (
          <ArticlesTable shipmentArticles={shipment.articles} />
        )}
      </Box>
    </ShipmentArticlesContext>
  );
};
